import { useTranslations } from 'next-intl';

import { useToolGroup } from '@/components/assistant-ui/tool-group-provider';
import { useLocalizedConfig } from '@/hooks/use-localized-config';
import { interpolateTemplate } from '@/lib/utils/domain';

/**
 * Returns `null` when the template needs `{name}` but no `assistantName` is
 * configured — callers fall back to their own default copy rather than render
 * a stub like "  is working on your request". Pass `requireOverride` to resolve
 * only an explicit tenant override (skipping the shared default template) —
 * used by the activity trail, which has its own generic fallback.
 *
 * Override resolution order:
 *   1. `{bucketCatalogNamespace}.thinking` from the tenant's trail next-intl
 *      namespace (per-locale, falls back to English via next-intl).
 *   2. the localized `chat.thinkingIndicatorText` config override (legacy).
 *   3. the shared `thinkingIndicator.default` template (unless requireOverride).
 */
export function useResolvedThinkingIndicatorText({
  requireOverride = false,
}: { requireOverride?: boolean } = {}): string | null {
  const t = useTranslations();
  const { bucketCatalogNamespace } = useToolGroup();
  const configOverride = useLocalizedConfig<string>('chat.thinkingIndicatorText');
  const assistantName = useLocalizedConfig<string>('chat.assistantName');

  // `t.has` checks for the key without reporting a MISSING_MESSAGE error (most
  // trail namespaces have no `thinking` key — those fall through to the config
  // override). `t.raw` then bypasses the ICU formatter so `{name}` doesn't trigger
  // a FORMATTING_ERROR — we interpolate ourselves below.
  let namespaceOverride: string | null = null;
  const thinkingKey = `${bucketCatalogNamespace}.thinking`;
  if (bucketCatalogNamespace && t.has(thinkingKey)) {
    const raw = t.raw(thinkingKey) as unknown;
    if (typeof raw === 'string' && raw.length > 0) namespaceOverride = raw;
  }

  const override = namespaceOverride ?? configOverride;
  if (requireOverride && !override) return null;

  const rawDefault = t.raw('thinkingIndicator.default') as unknown;
  const defaultTemplate = typeof rawDefault === 'string' ? rawDefault : '';
  const template = override || defaultTemplate;
  if (!template.includes('{name}')) return template;
  if (!assistantName) return null;
  return interpolateTemplate({ template, tokens: { name: assistantName } });
}
